import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { useDateFormat } from '@/hooks/useDateFormat';
import type { WeightProductionEntryDto } from '@/types/weightProduction';

/**
 * Read-only — there is no allocation step to confirm here, unlike GRN/Production/Job Work Putaway.
 * Lists every weighing regardless of whether its stock has actually posted yet: a Direct weighing
 * posts immediately (see WeightProductionEntryService.ConfirmEntryAsync), but a plan-based one
 * stays "Pending Job Completion" until a supervisor confirms it on the Job Completion screen — the
 * Stock Status column is what tells the two apart here.
 */
export default function WeightProductionPutawayPage() {
  const { t } = useTranslation('weightProduction');
  const { formatDate } = useDateFormat();

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['weight-production-entries'],
    queryFn: async () => (await apiClient.get<WeightProductionEntryDto[]>('/weight-production-entries')).data,
  });

  const columns: DataTableColumn<WeightProductionEntryDto>[] = [
    { field: 'barcodeNo', headerName: t('putaway.columns.barcode') },
    { field: 'materialCode', headerName: t('putaway.columns.material'), render: (r) => `${r.materialCode} — ${r.materialName}` },
    { field: 'batchNo', headerName: t('putaway.columns.batch'), render: (r) => r.batchNo ?? '—' },
    { field: 'expiryDate', headerName: t('putaway.columns.expiry'), render: (r) => (r.expiryDate ? formatDate(r.expiryDate) : '—') },
    { field: 'actualWeight', headerName: t('putaway.columns.qty'), render: (r) => `${r.actualWeight} ${r.weightUom}` },
    { field: 'warehouseName', headerName: t('putaway.columns.warehouse'), filterable: true },
    { field: 'locationCode', headerName: t('putaway.columns.zone'), render: (r) => r.locationCode ?? '—', filterable: true },
    { field: 'createdDate', headerName: t('putaway.columns.produced'), render: (r) => formatDate(r.createdDate) },
    {
      field: 'stockPosted', headerName: t('putaway.columns.stockStatus'), filterable: true,
      render: (r) => <StatusChip status={r.stockPosted ? t('putaway.stockPosted') : t('putaway.stockPending')} />,
    },
    { field: 'status', headerName: t('putaway.columns.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
  ];

  return (
    <>
      <PageHeader title={t('putaway.title')} subtitle={t('putaway.subtitle')} />
      <DataTable columns={columns} rows={entries} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="weight-production-putaway" />
    </>
  );
}
